// Hook para DetallePaciente.tsx — vista del kine sobre un paciente
// individual. Recibe patientId del path /paciente/:patientId.
//
// Combina: paciente + últimas N sesiones completadas + total de sesiones
// + nombre del kine logueado (para el header). 3 queries en paralelo.
//
// kine_id resuelto via useCurrentKineId() — devuelve null durante auth
// loading (B7 hotfix evita race con TEMP fallback).
//
// Trae auth_user_id + signup_token + signup_token_expires_at para que
// la UI condicione el botón "Regenerar invitación" / "Copiar link"
// (paciente sin signup completado) vs no mostrarlos (paciente activo).
//
// Sprint 3 Approach A: el shape ya NO expone `tratamiento`. La entidad
// tratamiento es contenedor invisible (ver deuda técnica "Deprecar
// tratamientos como entidad separada" en docs/decisions/). La UI del
// kine consume el "inicio del plan activo" via usePlanesActivos donde
// necesite referencia temporal (subtitle en DetalleContent).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentKineId } from "@/app/components/auth/AuthProvider";

const RECIENTES_LIMIT = 10;

export interface SesionReciente {
  id: string;
  ended_at: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  /** Nivel 1..10 reportado por el paciente, null si no confirmó. */
  dolor_reportado: number | null;
}

export interface DetallePacienteData {
  paciente: {
    nombre: string;
    apellido: string;
    email: string | null;
    telefono: string | null;
    fecha_nacimiento: string | null;
    notas_clinicas: string | null;
    /** "invited" si auth_user_id IS NULL Y signup_token IS NOT NULL,
     *  sino "active". Para condicionar UI de regenerar/copiar invitación. */
    invitationStatus: "invited" | "active";
    /** Solo presente si invitationStatus === "invited". */
    signupToken: string | null;
    signupTokenExpiresAt: string | null;
  };
  sesionesRecientes: SesionReciente[];
  totalSesiones: number;
  kineNombre: string;
}

interface State {
  data: DetallePacienteData | null;
  errorKind: "not-found" | "fetch-error" | null;
  errorMessage: string | null;
  loading: boolean;
}

export interface UseDetallePacienteReturn extends State {
  /** Re-corre el fetch. Útil después de editar/regenerar invitación. */
  refetch: () => Promise<void>;
}

interface PacienteRowRaw {
  nombre: string;
  apellido: string;
  email: string | null;
  telefono: string | null;
  fecha_nacimiento: string | null;
  notas_clinicas: string | null;
  auth_user_id: string | null;
  signup_token: string | null;
  signup_token_expires_at: string | null;
  sesiones: Array<{
    id: string;
    ended_at: string | null;
    total_reps: number;
    score_promedio: number | null;
    duration_sec: number | null;
    dolor_reportado: number | null;
  }>;
}

export function useDetallePaciente(
  patientId: string | undefined,
): UseDetallePacienteReturn {
  const kineId = useCurrentKineId();
  const [state, setState] = useState<State>({
    data: null,
    errorKind: null,
    errorMessage: null,
    loading: true,
  });

  const fetchAll = useCallback(
    async (signal?: { cancelled: boolean }) => {
      if (!patientId || !kineId) return;

      const [pacienteResult, kineResult, totalSesionesResult] =
        await Promise.all([
          supabase
            .from("pacientes")
            .select(
              `
              nombre, apellido, email, telefono, fecha_nacimiento, notas_clinicas,
              auth_user_id, signup_token, signup_token_expires_at,
              sesiones(id, ended_at, total_reps, score_promedio, duration_sec, estado, dolor_reportado)
            `,
            )
            .eq("id", patientId)
            .eq("sesiones.estado", "completada")
            .order("ended_at", {
              foreignTable: "sesiones",
              ascending: false,
            })
            .limit(RECIENTES_LIMIT, { foreignTable: "sesiones" })
            .maybeSingle()
            .returns<PacienteRowRaw>(),
          supabase
            .from("kines")
            .select("nombre")
            .eq("id", kineId)
            .maybeSingle(),
          supabase
            .from("sesiones")
            .select("*", { count: "exact", head: true })
            .eq("paciente_id", patientId)
            .eq("estado", "completada"),
        ]);

      if (signal?.cancelled) return;

      if (pacienteResult.error) {
        console.warn(
          "[useDetallePaciente] paciente fetch error",
          pacienteResult.error,
        );
        setState({
          data: null,
          errorKind: "fetch-error",
          errorMessage: pacienteResult.error.message,
          loading: false,
        });
        return;
      }

      if (!pacienteResult.data) {
        setState({
          data: null,
          errorKind: "not-found",
          errorMessage: null,
          loading: false,
        });
        return;
      }

      if (kineResult.error) {
        console.warn("[useDetallePaciente] kine fetch error", kineResult.error);
      }
      if (totalSesionesResult.error) {
        console.warn(
          "[useDetallePaciente] total sesiones error",
          totalSesionesResult.error,
        );
      }

      const raw = pacienteResult.data;
      const sesionesRecientes: SesionReciente[] = raw.sesiones
        .filter(
          (s): s is SesionReciente & { ended_at: string } =>
            s.ended_at !== null,
        )
        .map((s) => ({
          id: s.id,
          ended_at: s.ended_at,
          total_reps: s.total_reps,
          score_promedio: s.score_promedio,
          duration_sec: s.duration_sec,
          dolor_reportado: s.dolor_reportado,
        }));

      const invitationStatus: "invited" | "active" =
        raw.auth_user_id === null && raw.signup_token !== null
          ? "invited"
          : "active";

      const data: DetallePacienteData = {
        paciente: {
          nombre: raw.nombre,
          apellido: raw.apellido,
          email: raw.email,
          telefono: raw.telefono,
          fecha_nacimiento: raw.fecha_nacimiento,
          notas_clinicas: raw.notas_clinicas,
          invitationStatus,
          signupToken: invitationStatus === "invited" ? raw.signup_token : null,
          signupTokenExpiresAt:
            invitationStatus === "invited"
              ? raw.signup_token_expires_at
              : null,
        },
        sesionesRecientes,
        totalSesiones: totalSesionesResult.count ?? 0,
        kineNombre: kineResult.data?.nombre ?? "—",
      };

      setState({
        data,
        errorKind: null,
        errorMessage: null,
        loading: false,
      });
    },
    [patientId, kineId],
  );

  useEffect(() => {
    if (!patientId) {
      setState({
        data: null,
        errorKind: "not-found",
        errorMessage: "Falta el id del paciente en la URL",
        loading: false,
      });
      return;
    }
    if (!kineId) return;

    const signal = { cancelled: false };
    setState({
      data: null,
      errorKind: null,
      errorMessage: null,
      loading: true,
    });
    void fetchAll(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [patientId, kineId, fetchAll]);

  const refetch = useCallback(async () => {
    if (!patientId || !kineId) return;
    setState((s) => ({ ...s, loading: true }));
    await fetchAll();
  }, [patientId, kineId, fetchAll]);

  return { ...state, refetch };
}
